import each from 'lodash/each';
import fromPairs from 'lodash/fromPairs';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import sortBy from 'lodash/sortBy';
import toPairs from 'lodash/toPairs';
import uniq from 'lodash/uniq';
import mapValues from 'lodash/mapValues';
import omitBy from 'lodash/omitBy';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import endsWith from 'lodash/endsWith';

import store from '@core/store';

import i18n from '@plugins/i18n';

/**
 * Returns a filtered object with only the properties that are not null
 * or properties that don't end with ':undefined' or ':null'
 *
 * @param {Object} v
 * @returns {Object}
 */
export function removeInternalKeys(v) {
    v = mapValues(v, item => {
        if (!Array.isArray(item) && isObject(item)) {
            return removeInternalKeys(item);
        }
        return item;
    });

    return omitBy(v, isInternalValue);
}

/**
 * Returns true if the given item is classed as an internal value
 *
 * @param {Object} item
 * @returns {Boolean}
 */
export function isInternalValue(item) {
    if (typeof item === 'string' && item.startsWith('$') && item !== '$deleted') {
        return true;
    }

    return false;
}

/**
 * Returns a filtered object with only the properties that are not undefined
 * or properties that don't end with ':undefined'
 *
 * @param {Object} v
 * @returns {Object}
 */
export function removeUndefined(v) {
    v = mapValues(v, item => {
        if (!Array.isArray(item) && isObject(item)) {
            return removeUndefined(item);
        }
        return item;
    });

    return omitBy(v, isUndefinedValue);
}

/**
 * Returns true if the given item is classed as an undefined value
 *
 * @param {Object} item
 * @returns {Boolean}
 */
export function isUndefinedValue(item) {
    if (item === undefined) {
        return true;
    }

    if (endsWith(item, ':undefined')) {
        return true;
    }

    return false;
}

/**
 * Returns a filtered object with only the properties that are not null
 * or properties that don't end with ':undefined' or ':null'
 *
 * @param {Object} v
 * @returns {Object}
 */
export function removeNull(v) {
    v = mapValues(v, item => {
        if (!Array.isArray(item) && isObject(item)) {
            return removeNull(item);
        }
        return item;
    });

    return omitBy(v, isNullValue);
}

/**
 * Returns true if the given item is classed as a null value
 *
 * @param {Object} item
 * @returns {Boolean}
 */
export function isNullValue(item) {
    if (isNil(item)) {
        return true;
    }

    if (endsWith(item, ':undefined')) {
        return true;
    }

    if (endsWith(item, ':null')) {
        return true;
    }

    if (isObject(item) && has(item, 'prefix') && item.prefix === '+44' && Object.keys(item).length === 1) {
        return true;
    }

    if (isObject(item) && has(item, 'currency') && !has(item, 'amount')) {
        return true;
    }

    if (isObject(item) && has(item, 'region') && !has(item, 'national_number')) {
        return true;
    }

    if (isObject(item) && !has(item, 'region') && has(item, 'national_number')) {
        return true;
    }

    if (isObject(item) && has(item, 'region') && has(item, 'national_number') && item.national_number === null) {
        return true;
    }

    if (isObject(item) && has(item, 'region') && has(item, 'national_number') && item.region === null) {
        return true;
    }

    if (isObject(item) && isEmpty(item)) {
        return true;
    }

    return false;
}

/**
 * Returns a filtered object with only the properties that are not empty objects
 *
 * @param {Object} v
 * @returns {Object}
 */
export function removeEmptyObjects(obj) {
    const newObj = Array.isArray(obj) ? [] : {};

    Object.entries(obj).forEach(([key, value]) => {
        if (isObject(value) && !isArray(value)) {
            const child = removeEmptyObjects(value);
            if (!isEmpty(child)) {
                newObj[`${key}`] = child;
            }
        } else if (isArray(value)) {
            const nonEmptyItems = value.map(removeEmptyObjects).filter(item => !isEmpty(item));

            if (nonEmptyItems.length > 0) {
                newObj[`${key}`] = nonEmptyItems;
            }
        } else {
            newObj[`${key}`] = value;
        }
    });

    return newObj;
}

/**
 * Returns a filtered object with only the properties that are not empty strings
 *
 * @param {Object} v
 * @returns {Object}
 */
export function removeEmptyStrings(obj) {
    const newObj = Array.isArray(obj) ? [] : {};

    Object.entries(obj).forEach(([key, value]) => {
        if (isObject(value)) {
            const child = removeEmptyStrings(value);
            if (!Array.isArray(child) || !isEmpty(child)) {
                newObj[`${key}`] = child;
            }
        } else if (isString(value)) {
            if (value.trim() !== '') {
                newObj[`${key}`] = value;
            }
        } else {
            newObj[`${key}`] = value;
        }
    });

    return newObj;
}

/**
 * Flattens a nested object into a single-level object.
 *
 * @param {object} target The object to flatten.
 * @param {object} [options] Configuration options.
 * @param {string} [options.delimiter='.'] The string to separate keys.
 * @param {number} [options.maxDepth=Infinity] The maximum recursion depth.
 * @param {function} [options.transformKey=(key) => key] A function to transform each key segment.
 * @returns {object} The flattened object.
 */
export const flatten = (target, options = {}) => {
    const { delimiter = '.', maxDepth = Infinity, transformKey = key => key } = options;

    const output = {};

    const step = (object, prevKey, currentDepth) => {
        Object.entries(object).forEach(([key, value]) => {
            const newKey = prevKey ? prevKey + delimiter + transformKey(key) : transformKey(key);

            const canRecurse =
                value !== null && typeof value === 'object' && Object.keys(value).length > 0 && currentDepth < maxDepth;

            if (canRecurse) {
                step(value, newKey, currentDepth + 1);
            } else {
                output[`${newKey}`] = value;
            }
        });
    };

    // Ensure the target is a valid object before starting
    if (target !== null && typeof target === 'object') {
        step(target, null, 1);
    }

    return output;
};

/**
 * Unflattens an object with delimited keys into a nested object.
 *
 * @param {object} target The object to unflatten.
 * @param {object} [options] Configuration options.
 * @param {string} [options.delimiter='.'] The string separating keys.
 * @param {boolean} [options.overwrite=false] Whether to overwrite existing primitive values.
 * @param {boolean} [options.object=false] If true, treats numeric keys as strings (creates objects, not arrays).
 * @returns {object} The unflattened object.
 */
export const unflatten = (target, options = {}) => {
    const { delimiter = '.', overwrite = false, object = false } = options;

    if (Object.prototype.toString.call(target) !== '[object Object]') {
        return target;
    }

    /**
     * Safely parses a key segment.
     * Converts numeric strings to numbers for array creation,
     * unless options.object is true or it's an unsafe key.
     */
    const parseKeySegment = key => {
        if (key === 'constructor' || key === '__proto__') {
            return key;
        }

        const parsedKey = Number(key);

        return !object && !isNaN(parsedKey) && isFinite(parsedKey) && String(parsedKey) === key ? parsedKey : key;
    };

    const result = {};

    Object.entries(target).forEach(([key, value]) => {
        const keys = key.split(delimiter).map(parseKeySegment);
        let recipient = result;

        for (let i = 0; i < keys.length - 1; i++) {
            const currentKey = keys[`${i}`];
            const nextKey = keys[i + 1];

            const type = Object.prototype.toString.call(recipient[`${currentKey}`]);
            const isObject = type === '[object Object]' || type === '[object Array]';

            if (!overwrite && !isObject && typeof recipient[`${currentKey}`] !== 'undefined') {
                return;
            }

            if ((overwrite && !isObject) || (!overwrite && recipient[`${currentKey}`] == null)) {
                recipient[`${currentKey}`] = typeof nextKey === 'number' ? [] : {};
            }

            recipient = recipient[`${currentKey}`];
        }

        const lastKey = keys[keys.length - 1];

        const finalValue =
            Object.prototype.toString.call(value) === '[object Object]' ? unflatten(value, options) : value;

        if (overwrite || typeof recipient[`${lastKey}`] === 'undefined') {
            recipient[`${lastKey}`] = finalValue;
        }
    });

    return result;
};

export const diff = (lo, ro, options = {}) => {
    options = {
        retainArrayValues: false,
        retainObjectValues: false,
        removeNull: false,
        removeInternalKeys: false,
        removeEmptyStrings: false,
        removeEmptyObjects: false,
        ignoreKeys: [],
        ignorePaths: [],
        ...options
    };

    const shouldIgnorePath = currentPath => {
        return options.ignorePaths.some(ignorePath => {
            const ignorePathParts = ignorePath.split('.');
            const currentPathParts = currentPath.split('.');

            // For partial matching, we'll look for the pattern at the end of the path
            if (ignorePathParts[0] === '**') {
                // Remove the '**' and check if the current path ends with the remaining pattern
                const patternToMatch = ignorePathParts.slice(1);
                if (currentPathParts.length < patternToMatch.length) {
                    return false;
                }

                // Get the end of the current path that should match our pattern
                const endParts = currentPathParts.slice(-patternToMatch.length);

                // Compare each part
                return patternToMatch.every((part, index) => {
                    if (part === '[]') {
                        return !isNaN(endParts[`${index}`]);
                    }
                    return part === endParts[`${index}`];
                });
            }

            // Original exact matching logic
            if (currentPathParts.length !== ignorePathParts.length) {
                return false;
            }

            return ignorePathParts.every((part, index) => {
                if (part === '[]') {
                    return !isNaN(currentPathParts[`${index}`]);
                }
                return part === currentPathParts[`${index}`];
            });
        });
    };

    const diffHelper = (lo, ro, retainObjectValues = false, currentPath = '') => {
        if (currentPath && shouldIgnorePath(currentPath)) {
            return undefined;
        }

        if (lo === ro) {
            return undefined;
        }

        // If both values are arrays, compare each item in the arrays.
        if (Array.isArray(lo) && Array.isArray(ro)) {
            const result = [];
            let arrayChanged = false;
            for (let i = 0; i < Math.max(lo.length, ro.length); i++) {
                const itemPath = currentPath ? `${currentPath}.${i}` : `${i}`;
                const itemDiff = diffHelper(lo[`${i}`], ro[`${i}`], options.retainObjectValues, itemPath);

                // If there is a difference between the two items, record it.
                if (itemDiff !== undefined) {
                    arrayChanged = true;
                    // If retainArrayValues is true and both lo[i] and itemDiff are objects,
                    // merge the objects before pushing to the result array.
                    if (options.retainArrayValues && typeof lo[`${i}`] === 'object' && typeof itemDiff === 'object') {
                        result.push({ ...lo[`${i}`], ...itemDiff });
                    } else {
                        result.push(itemDiff);
                    }
                } else if (options.retainArrayValues) {
                    // If retainArrayValues is true, add the unchanged item to the result array.
                    result.push(lo[`${i}`]);
                }
            }

            // If the array has changed, return the result array. Otherwise, return undefined.
            return arrayChanged ? result : undefined;
        }

        // If both values are objects (but not arrays), compare their properties.
        if (lo && ro && typeof lo === 'object' && typeof ro === 'object') {
            const keys = new Set([...Object.keys(lo), ...Object.keys(ro)]);

            const result = {};
            let objectChanged = false;

            // Iterate through all the unique keys in both objects.
            for (const key of keys) {
                if (options.ignoreKeys.includes(key)) {
                    continue;
                }
                const keyPath = currentPath ? `${currentPath}.${key}` : key;
                const itemDiff = diffHelper(lo[`${key}`], ro[`${key}`], options.retainObjectValues, keyPath);

                if (itemDiff !== undefined) {
                    objectChanged = true;
                    result[`${key}`] = itemDiff;
                }
            }

            if (objectChanged && retainObjectValues) {
                // If objectChanged and retainObjectValues are true, include all other values from the same object.
                return { ...lo, ...ro, ...result };
            } else if (objectChanged && 'currency' in lo && 'amount' in lo) {
                // Currency objects should be treated as individual
                return { ...lo, ...result };
            } else if (objectChanged && 'region' in lo && 'national_number' in lo) {
                // Phone number objects should be treated as individual
                return { ...lo, ...result };
            }

            // If the object has changed, return the result object. Otherwise, return undefined.
            return objectChanged ? result : undefined;
        }

        // If the values are not equal, return the right-side value as the difference.
        if (lo !== ro) {
            return ro;
        }

        return undefined;
    };

    if (options.removeNull) {
        lo = removeNull(lo);
        ro = removeNull(ro);
    }

    if (options.removeInternalKeys) {
        lo = removeInternalKeys(lo);
        ro = removeInternalKeys(ro);
    }

    if (options.removeEmptyStrings) {
        lo = removeEmptyStrings(lo);
        ro = removeEmptyStrings(ro);
    }

    if (options.removeEmptyObjects) {
        lo = removeEmptyObjects(lo);
        ro = removeEmptyObjects(ro);
    }

    // Call the diffHelper function with the input objects and return the result.
    // If the result is undefined, return an empty object.
    let output = diffHelper(lo, ro, options.retainObjectValues);

    return output !== undefined ? output : {};
};

export const getValuesByKey = (data, targetKey) => {
    let values = [];

    function recursiveSearch(obj) {
        if (Array.isArray(obj)) {
            obj.forEach(item => recursiveSearch(item));
        } else if (typeof obj === 'object' && obj !== null) {
            Object.keys(obj).forEach(key => {
                if (key === targetKey) {
                    values.push(obj[`${key}`]);
                } else {
                    recursiveSearch(obj[`${key}`]);
                }
            });
        }
    }

    recursiveSearch(data);
    return values;
};

/**
 * Returns a list of all the keys in an object or array.
 * @param {Object, Array} value
 */
export const getKeys = value => {
    let found = [];

    if (isObject(value)) {
        found = keys(value);
        each(value, item => {
            found = found.concat(getKeys(item));
        });
    } else if (isArray(value)) {
        each(value, item => {
            found = found.concat(getKeys(item));
        });
    }

    return uniq(found);
};

/**
 * Returns true if the given value is an object and has at least one File
 * @param {Object} value
 */
export const findObjectValuesByType = (value, type) => {
    try {
        const flatValue = flatten(value);

        const files = pickBy(flatValue, v => {
            return v instanceof type;
        });

        if (Object.keys(files).length > 0) {
            return unflatten(files);
        }

        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Sort an object by its keys
 * @param {Object}
 */
export const sortObjectByKeys = obj => fromPairs(sortBy(toPairs(obj), 0));

/**
 * Sort an array by key order
 * @param {Object}
 */
export const sortByKeyOrder = (items, key, keyOrder = []) => {
    return items.sort((a, b) => {
        const keyA = a[`${key}`];
        const keyB = b[`${key}`];
        const orderA = keyOrder.indexOf(keyA);
        const orderB = keyOrder.indexOf(keyB);

        if (orderA !== -1 && orderB !== -1) {
            return orderA - orderB;
        }

        return 0;
    });
};

export const clone = (data, seen = new Map()) => {
    if (data === null || typeof data !== 'object') {
        return data;
    }

    if (seen.has(data)) {
        return seen.get(data);
    }

    if (Array.isArray(data)) {
        const newArray = [];
        seen.set(data, newArray);
        for (const item of data) {
            newArray.push(clone(item, seen));
        }
        return newArray;
    }

    const cloned = {};
    seen.set(data, cloned);

    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            if (typeof data[`${key}`] !== 'function') {
                cloned[`${key}`] = clone(data[`${key}`], seen);
            }
        }
    }

    return Object.keys(cloned).length ? cloned : null;
};

export const collectItems = async (requestItems = null, options = {}) => {
    if (!requestItems) {
        return;
    }

    options = {
        id: null,
        filters: {},
        limit: 100,
        sort: [],
        beforeRequest: () => {},
        afterRequest: () => {},
        requestConfig: {},
        ...options
    };

    let items = [];
    let total = 1;
    let offset = 0;

    try {
        while (items.length < total) {
            if (options.id && store.getters['app/aborted'].includes(options.id)) {
                return;
            }

            options.beforeRequest(items.length, total, options.limit);

            const response = await requestItems(
                offset,
                options.limit,
                options.sort,
                options.filters,
                options.requestConfig ?? {}
            );

            if (response === null) {
                return;
            }

            if (!('data' in response)) {
                throw new Error(i18n.t('response_not_recognised'));
            }

            if (!('metadata' in response)) {
                if (!Array.isArray(response.data)) {
                    throw new Error(i18n.t('response_not_recognised'));
                } else {
                    total = response.data.length;
                }
            } else if (response.metadata && response.metadata.total !== null) {
                total = response.metadata.total;
            } else {
                total = 0;
            }

            items.push(...response.data);
            offset = items.length;

            options.afterRequest(items.length, total, options.limit);
        }
    } catch (error) {
        console.error(error);
    }

    return items;
};
